import styled from "styled-components";

interface ContentContainerProps {
  direction: "column" | "row" | "row-reverse";
}

interface TextContainerProps {
  $textAlign?: "left" | "center" | "right";
}

interface MediaSizeProps {
  $maxWidth?: number;
}

interface VideoProps extends MediaSizeProps {
  $videoType?: string;
}

export const ContentContainer = styled.div<ContentContainerProps>`
  width: 100%;
  max-width: 2560px;
  margin: 0 auto;
  padding: 80px 0;
  display: flex;
  flex-direction: ${(props) => props.direction};
  align-items: center;
  gap: 80px;

  @media (max-width: 1920px) {
    padding: 60px 0;
    gap: 60px;
  }

  @media (max-width: 1440px) {
    padding: 50px 0;
    gap: 50px;
  }

  @media (max-width: 1024px) {
    padding: 40px 0;
    gap: 40px;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
    padding: 30px 0;
    gap: 30px;
  }

  @media (max-width: 480px) {
    padding: 20px 0;
    gap: 20px;
  }
`;

export const TextContainer = styled.div<TextContainerProps>`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: ${(props) =>
    props.$textAlign === "center"
      ? "center"
      : props.$textAlign === "right"
      ? "flex-end"
      : "flex-start"};
  text-align: ${(props) => props.$textAlign || "left"};
  width: ${(props) => (props.$textAlign === "left" ? "900px" : null)};

  @media (max-width: 768px) {
    align-items: center;
    width: 100%;
  }

  @media (max-width: 480px) {
    width: 100%;
    padding: 0 20px;
    box-sizing: border-box;
  }
`;

export const GraySection = styled.div`
  width: 100%;
  background-color: #f6f6f6;
  padding: 80px 0;
  margin: 80px 0;
  display: flex;
  flex-direction: column;
  align-items: center;

  @media (max-width: 768px) {
    padding: 40px 0;
    margin: 40px 0;
  }

  @media (max-width: 480px) {
    padding: 20px 0;
    margin: 20px 0;
  }
`;

export const MainTitle = styled.h2<TextContainerProps>`
  font-size: 56px;
  font-weight: 700;
  font-family: "Pretendard";
  font-feature-settings: "liga" off, "clig" off;
  text-align: ${(props) => props.$textAlign || "left"};
  line-height: 1.4;
  margin-bottom: 20px;
  white-space: pre-line;

  @media (max-width: 1920px) {
    font-size: 48px;
    line-height: 1.4;
    margin-bottom: 20px;
  }

  @media (max-width: 1440px) {
    font-size: 36px;
    line-height: 1.35;
    margin-bottom: 18px;
  }

  @media (max-width: 1024px) {
    font-size: 32px;
    line-height: 1.25;
    margin-bottom: 14px;
  }

  @media (max-width: 768px) {
    font-size: 28px;
    line-height: 1.2;
    margin-bottom: 12px;
    padding: 0 20px;
    word-break: keep-all;
  }

  @media (max-width: 480px) {
    font-size: 24px;
    line-height: 1.15;
    margin-bottom: 10px;
    padding: 0 10px;
  }
`;

export const SubTitle = styled.h3<TextContainerProps>`
  font-size: 32px;
  font-weight: 600;
  font-family: "Pretendard";
  text-align: ${(props) => props.$textAlign || "left"};
  color: #666;
  margin-bottom: 20px;
  white-space: pre-line;
  line-height: 1.5;

  @media (max-width: 1920px) {
    font-size: 24px;
    line-height: 1.5;
    margin-bottom: 18px;
  }

  @media (max-width: 1440px) {
    font-size: 20px;
    line-height: 1.45;
    margin-bottom: 16px;
  }

  @media (max-width: 1024px) {
    font-size: 18px;
    line-height: 1.35;
    margin-bottom: 14px;
  }

  @media (max-width: 768px) {
    font-size: 16px;
    line-height: 1.3;
    margin-bottom: 12px;
    padding: 0 20px;
    word-break: keep-all;
  }

  @media (max-width: 480px) {
    font-size: 14px;
    line-height: 1.25;
    margin-bottom: 10px;
    padding: 0 10px;
  }
`;

export const MediaContainer = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  gap: 40px;
  flex-wrap: wrap;
  max-width: 100%;
`;

export const Image = styled.img<MediaSizeProps>`
  max-width: ${(props) => props.$maxWidth || 1200}px;
  width: 100%;
  height: auto;
  border-radius: 20px;

  @media (max-width: 1920px) {
    max-width: ${(props) =>
      props.$maxWidth ? props.$maxWidth * 0.83 : 1000}px;
  }

  @media (max-width: 1440px) {
    max-width: ${(props) => (props.$maxWidth ? props.$maxWidth * 0.67 : 800)}px;
  }

  @media (max-width: 1024px) {
    max-width: ${(props) => (props.$maxWidth ? props.$maxWidth * 0.5 : 600)}px;
  }

  @media (max-width: 768px) {
    max-width: 100%;
    border-radius: 0;
  }
`;

export const Video = styled.video<VideoProps>`
  max-width: ${(props) => props.$maxWidth || 1200}px;
  height: 100%;
  border-radius: 20px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);

  ${(props) =>
    props.$videoType === "live_use" &&
    `

    @media (max-width: 1920px) {
      width: 80%;
    }

    @media (max-width: 1440px) {
      width: 80%;
    }

    @media (max-width: 768px) {
      width: 70%;
    }

    @media (max-width: 480px) {
      width: 60%;
    }
  `}

  @media (max-width: 1920px) {
    max-width: ${(props) =>
      props.$maxWidth ? props.$maxWidth * 0.83 : 1000}px;
  }

  @media (max-width: 1440px) {
    max-width: ${(props) => (props.$maxWidth ? props.$maxWidth * 0.67 : 800)}px;
  }

  @media (max-width: 1024px) {
    max-width: ${(props) => (props.$maxWidth ? props.$maxWidth * 0.5 : 600)}px;
  }

  @media (max-width: 768px) {
    max-width: 100%;
  }

  @media (max-width: 480px) {
    max-width: 100%;
  }
`;

export const ButtonContainer = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 20px;

  @media (max-width: 768px) {
    width: 100%;
    gap: 8px;
    justify-content: center;
    flex-wrap: wrap;
  }
`;

export const KioskButton = styled.button<{ $isSelected?: boolean }>`
  background-color: ${(props) => (props.$isSelected ? "#000" : "#e7e7e7")};
  border: none;
  border-radius: 8px;
  font-size: 20px;
  width: 180px;
  font-weight: 800;
  color: ${(props) => (props.$isSelected ? "white" : "#000")};
  cursor: pointer;
  padding: 15px 20px;
  transition: background-color 0.3s, color 0.3s;
  white-space: nowrap;

  @media (max-width: 1920px) {
    font-size: 18px;
    width: 180px;
    padding: 14px 18px;
  }

  @media (max-width: 1440px) {
    font-size: 16px;
    width: 160px;
    padding: 13px 16px;
  }

  @media (max-width: 1024px) {
    font-size: 14px;
    width: 140px;
    padding: 12px 14px;
  }

  @media (max-width: 768px) {
    font-size: 12px;
    width: 120px;
    padding: 10px 12px;
  }

  @media (max-width: 480px) {
    font-size: 11px;
    width: 100px;
    padding: 8px 10px;
  }

  &:hover {
    background-color: #000;
    color: white;
  }
`;

export const BannerGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 40px;
  margin-top: 80px;
  max-width: 1200px;
  margin: 80px auto 0;
  padding: 0 20px;

  @media (max-width: 1920px) {
    gap: 34px;
    margin-top: 60px;
  }

  @media (max-width: 1440px) {
    gap: 32px;
    margin-top: 50px;
  }

  @media (max-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 30px;
    margin-top: 40px;
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 25px;
    margin-top: 30px;
  }

  @media (max-width: 480px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
    margin-top: 20px;
    padding: 0 10px;
  }
`;

export const BannerImage = styled.img`
  width: 100%;
  max-width: 180px;
  height: auto;
  border-radius: 8px;
  transition: transform 0.3s;
  margin: 0 auto;
  display: block;

  @media (max-width: 1920px) {
    max-width: 160px;
  }

  @media (max-width: 1440px) {
    max-width: 140px;
  }

  @media (max-width: 1024px) {
    max-width: 130px;
  }

  @media (max-width: 768px) {
    max-width: 110px;
  }

  @media (max-width: 480px) {
    max-width: 100px;
  }

  &:hover {
    transform: scale(1.05);
  }
`;

export const StatBox = styled.div`
  @media (max-width: 480px) {
    width: 100%;
    max-width: 100%;
    height: auto;
    min-height: 180px;
    padding: 20px;
    margin: 0;
  }
`;
